import type { WorkbookLayerPath } from '@disy/cadenza.js';
import { isEqual } from '@disy/lib/is-equal';

import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import { getLayerName, getViewById, getViewByLayerId } from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export function getLayerPath(state: RootStateOr<WorkbookState>, layerIdToFind: LayerId): WorkbookLayerPath {
  const view = getViewByLayerId(state, layerIdToFind);
  return getLayerIdPath(layerIdToFind, view.layerTree)!.map((layerId) => getLayerName(state, view.id, layerId));
}

function getLayerIdPath(layerIdToFind: LayerId, layerTree: WorkbookLayerTreeNode[]) {
  for (const root of layerTree) {
    for (const path of generateLayerIdPaths(root)) {
      if (path.at(-1) === layerIdToFind) {
        return path;
      }
    }
  }
}

export function getLayerId(state: RootStateOr<WorkbookState>, viewId: ViewId, layerPath: WorkbookLayerPath) {
  const view = getViewById(state, viewId);
  const paths = view.layerTree
    .flatMap((root) => [...generateLayerIdPaths(root)])
    .filter((path) =>
      isEqual(
        layerPath,
        path.map((layerId) => getLayerName(state, viewId, layerId)),
      ),
    );
  if (paths.length === 1) {
    // Consider only unique matches.
    return paths[0].at(-1);
  }
}

function* generateLayerIdPaths(node: WorkbookLayerTreeNode, path: LayerId[] = []): Generator<LayerId[]> {
  path = [...path, node.id];
  if (node.children.length) {
    for (const child of node.children) {
      yield* generateLayerIdPaths(child, path);
    }
  } else {
    yield path;
  }
}
